import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useCopy } from "@/lib/useCopyToClipBoard";
import { BsBank } from "react-icons/bs";
import { useAuthState } from "@/context";
import Img2 from "../../../../assets/qr.png";
import { CopyIcon, CopyIcon2 } from "@/assets";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAccountName,
  getAllBank,
  getCryptoPayInAddress,
  getFiatPayIn,
} from "@/services/Home.service";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Plus,
  Wallet,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import Selector from "@/components/Selector";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { ToastMessage, type } from "@/lib/toastr";

const Withdraw = ({ balance }) => {
  const [selectedCurrency, setSelectedCurrency] = useState({});
  const [selectedNetwork, setSelectedNetwork] = useState({});
  const [selectedBank, setSelectedBank] = useState({});
  const { useCurrencies } = useAuthState();
  const { copyToClipboard } = useCopy();
  const [account, setAccount] = useState("");
  const [address, setAddress] = useState("");
  const [accountName, setAccountName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const currenciesList = useCurrencies()?.data?.data?.data?.map((i) => {
    return {
      label: `${i?.currency?.name} (${i?.currency?.code})`,
      value: i?.currency?.code,
      icon: i?.currency?.icon,
      type: i?.currency?.type,
    };
  });
  const bankRes = useQuery({
    queryKey: ["bank"],
    queryFn: async () => await getAllBank(),
    refetchOnMount: "always",
  });

  const allBanks = new Set();
  const filteredArr = bankRes?.data?.data?.data?.filter((el) => {
    const duplicate = allBanks.has(el.code);
    allBanks.add(el.code);
    return !duplicate;
  });
  const filtered = balance?.filter((i) => i.code === selectedCurrency?.value);

  useEffect(() => {
    if (account?.length === 10) {
      setIsLoading(true);
      getAccountName({
        bank: selectedBank?.value,
        account,
      })
        .then((res) => {
          setIsLoading(false);
          setAccountName(res?.data?.data?.account_name);
        })
        .catch((err) => {
          setIsLoading(false);
          setAccountName("");
          ToastMessage(
            type.ERROR,
            err.response.data.message === "Unable to verify account details"
              ? "Check your account details and try again!"
              : err.response.data.message
          );
        });
    }
  }, [account]);
  return (
    <CustomModal
      button={
        <Button
          variant="outline"
          className="flex gap-2 items-center text-primary hover:text-primary"
        >
          <Download />
          Withdraw
        </Button>
      }
      size="sm"
    >
      <div className="flex flex-col gap-8">
        <DialogHeader className="bg-off-white-1 py-6 px-8 rounded-t-[16px] flex flex-row items-center gap-4">
          <div className="w-12 h-12 rounded-full flex justify-center items-center bg-off-white-3 text-primary-3">
            <BsBank />
          </div>
          <div className="grid gap-[2px]">
            <DialogTitle className="text-lg font-medium text-primary-3">
              Withdraw
            </DialogTitle>

            <DialogDescription className="text-sm text-gray-80">
              Top up your available balance{" "}
            </DialogDescription>
          </div>
        </DialogHeader>
      </div>
      <div className="px-8 py-3 grid gap-6">
        <div className="flex flex-col w-full items-start gap-1 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
          <Label className="text-xs text-gray-70">Asset</Label>
          <Selector
            options={currenciesList ?? []}
            selected={selectedCurrency}
            setSelected={setSelectedCurrency}
          />
        </div>
        {selectedCurrency?.type === "crypto" && (
          <div className="flex flex-col w-full items-start gap-1 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
            <Label className="text-xs text-gray-70">Select Network</Label>
            <Selector
              options={[
                { label: "BEP-20", value: "bep20" },
                { label: "TRC-20", value: "trc20" },
                { label: "ERC-20", value: "erc20" },
              ]}
              selected={selectedNetwork}
              setSelected={setSelectedNetwork}
            />
          </div>
        )}
        <Separator orientation="horizontal" className="h-[1px] bg-border" />
        {selectedCurrency?.value === undefined ? null : (
          <div className="py-3 flex flex-col justify-between gap-6">
            <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 mb-4">
              <Label className="text-xs text-gray-70">Amount</Label>

              <div className="flex w-full items-center justify-between gap-3">
                <Input
                  className="p-0 border-none bg-transparent rounded-none h-6 w-full pb-2"
                  placeholder="0.00"
                />

                <span className="text-xs text-gray-60 w-[45%] text-right">
                  {selectedCurrency?.value}
                </span>
              </div>

              <Label className="text-xs text-gray-70 flex items-center gap-2 ">
                <Wallet className="w-3 h-3" />
                Available Balance:{" "}
                {filtered[0]?.balance?.toLocaleString("en-US")}
              </Label>
            </div>
            {selectedCurrency?.type === "fiat" ? (
              <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 ">
                <Label className="text-xs text-gray-70">Bank Name</Label>

                <Selector
                  options={
                    filteredArr?.map((i) => {
                      return { label: i.name, value: i.code };
                    }) || []
                  }
                  selected={selectedBank}
                  setSelected={setSelectedBank}
                />
              </div>
            ) : null}
            <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 ">
              <Label className="text-xs text-gray-70">
                {selectedCurrency?.type === "fiat"
                  ? "Account Number"
                  : "Wallet Address"}
              </Label>
              <Input
                className="p-0 border-none bg-transparent rounded-none h-6 w-full pb-2"
                onChange={(e) =>
                  selectedCurrency?.type === "fiat"
                    ? setAccount(e?.target?.value)
                    : setAddress(e?.target?.value)
                }
                placeholder={
                  selectedCurrency?.type === "fiat"
                    ? "Enter account number"
                    : "Enter wallet address"
                }
                value={account}
              />
            </div>
            {selectedCurrency?.type === "fiat" ? (
              <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
                <Label className="text-xs text-gray-70">Account Name</Label>
                <div className="flex w-full items-center justify-between">
                  <div className=" max-w-sm  w-full">
                    <Input
                      className="p-0 border-none h-4 bg-transparent "
                      disabled
                      value={accountName}
                    />
                  </div>
                  <span className="text-primary text-xs ">
                    {isLoading ? (
                      <Loader2 className="animate-spin w-4 h-4" />
                    ) : null}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
      <DialogFooter className="px-6 py-4 flex flex-row justify-between items-center bg-off-white-1 rounded-b-[16px]">
        <DialogClose>
          <Button
            type="button"
            variant="ghost"
            className=" text-primary0 h-12 rounded-[10px] p-[8px 12px] hover:text-primary0"
          >
            <ChevronLeft /> Cancel
          </Button>
        </DialogClose>
        <Button className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]">
          Confirm
          <ChevronRight />
        </Button>
      </DialogFooter>
    </CustomModal>
  );
};

export default Withdraw;
